import { SeoLandingLayout, type SeoPageContent } from '@/components/seo/SeoLandingLayout'

const content: SeoPageContent = {
  locale: 'en',
  path: '/family-budget-app',
  alternatePath: '/familienbudget-app',
  seo: {
    title: 'Family budget app — plan household costs together | Equna',
    description:
      'Equna is the family budget app for parents: log shared expenses, plan budgets per area, store receipts and close the month together. Made in Germany.',
  },
  hero: {
    eyebrow: 'For families',
    h1: 'Family budget app — overview instead of surprises',
    sub: 'Groceries, rent, kids, leisure: Equna turns a chaos of single expenses into a real family budget with clear areas, warnings and a monthly close.',
    note: 'Start free. GDPR-compliant. Made in Germany.',
  },
  problems: {
    heading: 'Why family budgets are hard to keep',
    intro:
      'Families have many small expenses across many areas — and new ones keep showing up. Without a system, you only see where the money went when the bank statement arrives.',
    items: [
      {
        title: 'Lots of small expenses',
        description:
          "Drugstore, school supplies, gifts, birthdays — each is small, the sum decides the month.",
      },
      {
        title: 'No real-time view',
        description:
          "You only realise the grocery budget melted away at month-end. By then it's too late.",
      },
      {
        title: 'Different split rules',
        description:
          'Some areas are split 50/50, others by income — too messy to maintain by hand.',
      },
      {
        title: 'No shared truth',
        description:
          "Each parent has their own sense of the state. 'Feels like too much' turns into a fight.",
      },
    ],
  },
  solutions: {
    heading: 'How Equna creates overview for families',
    intro:
      'Three or more budget areas, a clear split rule, and a monthly close both parents confirm.',
    items: [
      {
        icon: 'home',
        title: 'Budgets per area',
        description:
          'Groceries, rent, leisure, kids — define budgets where they hurt and see immediately where you stand.',
      },
      {
        icon: 'split',
        title: 'Global split',
        description:
          'One rule for all shared expenses. Override per expense when needed.',
      },
      {
        icon: 'kids',
        title: 'Per-child expenses',
        description:
          'School, hobbies, doctor — recorded per child and cleanly analysed.',
      },
      {
        icon: 'receipt',
        title: 'Receipts attached',
        description:
          'Paper receipt or online invoice as a photo on the expense. No more shoebox.',
      },
      {
        icon: 'calendar',
        title: 'Monthly close',
        description:
          'Both parents confirm the month. Equna calculates the balance and archives the state.',
      },
      {
        icon: 'shield',
        title: 'GDPR-compliant',
        description:
          'Data on German servers. Currently no ads, no tracking, no third-party sales.',
      },
    ],
  },
  comparison: {
    heading: 'Before vs. with Equna',
    columns: ['Without a family budget app', 'With Equna'],
    rows: [
      {
        topic: 'Overview',
        without: 'Only at month-end on the bank statement',
        with: 'State visible anytime in the dashboard',
      },
      {
        topic: 'Budgets',
        without: 'In your head — forgotten',
        with: 'Defined per area, with warnings',
      },
      {
        topic: 'Split rule',
        without: 'Debated per expense',
        with: 'Set once, applied to everything',
      },
      {
        topic: 'Receipts',
        without: 'In a shoebox or mail folder',
        with: 'Stored on the expense',
      },
      {
        topic: 'Monthly cutoff',
        without: 'Rarely happens',
        with: 'Both confirm, archived',
      },
    ],
  },
  trust: {
    heading: 'Why families trust Equna',
    items: [
      {
        title: 'Made in Germany',
        description: 'Built and operated in Berlin. Servers with IONOS in Germany.',
      },
      {
        title: 'GDPR-compliant',
        description: 'Expenses and receipts stay with you. No sharing for ad purposes.',
      },
      {
        title: 'Both parents equal',
        description: 'No main user. Both accounts have the same rights and the same view.',
      },
      {
        title: 'Free to start',
        description: 'The free tier covers everything families need day-to-day.',
      },
    ],
  },
  relatedBlog: {
    heading: 'Read more',
    posts: [
      {
        title: 'Split household costs — five models',
        description: 'Which split model fits your family? Four examples and a recommendation.',
        href: '/blog/split-household-costs-five-models',
      },
      {
        title: '3-account model and Equna',
        description: 'Why a shared family account plus Equna is the clean combination.',
        href: '/blog/3-account-model-and-equna-why-they-belong-together',
      },
      {
        title: 'Equna vs. Tricount',
        description: 'Tricount is great for trips — Equna is built for ongoing family costs.',
        href: '/blog/equna-vs-tricount-for-couples-and-families',
      },
    ],
  },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'How many budget areas are included?',
        answer:
          'The free version includes three budget areas — enough for most families (e.g. groceries, rent, leisure). The premium tier allows more areas and annual budgets.',
      },
      {
        question: 'Can we record expenses per child?',
        answer:
          'Yes. You can add children as profiles and assign expenses to a specific child. This is useful for co-parents who live separately too.',
      },
      {
        question: 'Do we get a warning when a budget runs low?',
        answer:
          'Yes. Equna notifies you when a budget is overspent early in the month. Both parents can decide whether to re-plan.',
      },
      {
        question: 'How does the monthly close work?',
        answer:
          "At month-end, both parents confirm that everything is logged. Equna calculates the transfer and archives the month's state. Later edits are locked.",
      },
      {
        question: 'Is Equna free?',
        answer:
          'Yes. The free tier is free and covers all family features. Premium is optional for longer receipt storage and advanced analytics.',
      },
    ],
  },
  finalCta: {
    heading: 'A family budget that actually works',
    sub: 'Three minutes of setup, then logging is the easy part.',
  },
  uiStrings: {
    backToHome: '← Back to home',
    related: 'Read more',
    legalPrivacy: 'Privacy',
    legalTerms: 'Terms',
    legalImprint: 'Imprint',
    legalSupport: 'Support',
    copyrightRights: 'All rights reserved.',
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Family budget app',
  },
}

export default function FamilyBudgetSeoPage() {
  return <SeoLandingLayout content={content} />
}
